import './ip-input.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select, Tooltip } from 'antd';
import { AddressIPType } from '../../../../utils/enums';
import {
    getIpPair,
    getUpdatedRangeAddress,
    getUpdatedSubnetAddress,
    isIpv4,
    isIpv6
} from '../../../../utils/addressesUtil';
import Arrow from '@svg/chevron-down_20.svg?react';
import Info from '@svg/info-blue.svg?react';
import { Link } from 'react-router-dom';
import { envKnowledgeBaseLink } from '../../../../utils/envLinksUtil';

interface IpInputProps {
    accessor: string;
    value?: string;
    placeholder?: string;
    disabled?: boolean;
    ipType: AddressIPType;
    editProp: (accessor: string, value: any) => void;
}

const getAddressCount = (bits: number, version: 'ipv4' | 'ipv6') => {
    if (version === 'ipv4') {
        return Math.pow(2, 32 - bits);
    } else {
        let result = BigInt(1);
        const exp = 128 - bits;
        for (let i = 0; i < exp; i++) {
            result *= BigInt(2);
        }
        return result;
    }
};

const getSubnetValues = (version: 'ipv4' | 'ipv6', start: number) =>
    Array.from({ length: 16 }, (_, i) => {
        const bits = i + start;
        const addressCount = getAddressCount(bits, version);
        return {
            label: `/${bits} (${addressCount.toLocaleString()} addresses)`,
            value: `${bits}`
        };
    }).reverse();

const ipv4SubnetValues = getSubnetValues('ipv4', 16);
const ipv6SubnetValues = getSubnetValues('ipv6', 112);

export const IpInput = ({
                             accessor,
                             value,
                             placeholder,
                             disabled,
                             ipType,
                             editProp,
                         }: IpInputProps) => {

    const [ipVersion, setIpVersion] = useState<number | undefined>(undefined);

    const IpPair = useMemo(() => {
        return getIpPair(ipType, value!);
    }, [value]);

    const handleChange = (newValue: string, index = 0) => {
        const filteredFromSpecialCharacters = newValue.replace(/[/-]/g, '');
        switch (ipType) {
            case AddressIPType.IP_SUBNET:
                onChangeSubnet(filteredFromSpecialCharacters, index);
                break;
            case AddressIPType.IP_RANGE:
                onChangeRange(filteredFromSpecialCharacters, index);
                break;
            case AddressIPType.IP_NETMASK:
                editProp(accessor, filteredFromSpecialCharacters);
                break;
        }
    };

    const onChangeRange = (newValue: string, index: number) => {
        const newRange = getUpdatedRangeAddress(newValue, index, IpPair);
        editProp(accessor, newRange);
    };

    const updateIpVersion = (index: number, newValue: string) => {
        if (index === 0) {
            if (newValue && isIpv4(newValue)) {
                setIpVersion(4);
            } else if (newValue && isIpv6(newValue)) {
                setIpVersion(6);
            } else {
                setIpVersion(undefined);
            }
        }
    };

    useEffect(() => {
        updateIpVersion(0, IpPair[0]);
    }, [IpPair]);

    const onChangeSubnet = (newValue: string, index: number) => {
        const newAddress = getUpdatedSubnetAddress(newValue, index, IpPair);
        editProp(accessor, newAddress);
    };

    const subnetValues = useMemo(
        () => {
            if (ipVersion === 4) {
                return ipv4SubnetValues;
            }
            if (ipVersion === 6) {
                return ipv6SubnetValues;
            }
            return [];
        }, [ipVersion]);

    const selectedSubnetValue = useMemo(() => {
        if (ipVersion && IpPair[1] && IpPair[1] !== '') {
            return IpPair[1];
        }
        return undefined;
    },[ipVersion, IpPair]);

    return <>
        {ipType === AddressIPType.IP_NETMASK && <Input className="ip-input single" value={value} placeholder={placeholder} onChange={(e) => handleChange(e.target.value)}/>}

        {ipType === AddressIPType.IP_RANGE &&
        <><Input className="ip-input range" value={IpPair[0]} placeholder={placeholder}
                 onChange={(e) => handleChange(e.target.value, 0)} disabled={disabled}/>
            <span>-</span>
            <Input className="ip-input range" value={IpPair[1]} placeholder={placeholder}
                   onChange={(e) => handleChange(e.target.value, 1)} disabled={disabled}/></>}

        {ipType === AddressIPType.IP_SUBNET &&
        <div className={'subnet-pair'}>
            <Input className="ip-input subnet" value={IpPair[0]} placeholder={placeholder}
                 onChange={(e) => handleChange(e.target.value, 0)} disabled={disabled}/>
            <Select
                className={'ip-input subnet table-dropdown'}
                popupClassName={'popup-table-dropdown dropdown-custom'}
                value={selectedSubnetValue}
                placeholder={'Select'}
                onChange={(newValue) => onChangeSubnet(newValue, 1)}
                options={subnetValues}
                disabled={!ipVersion || disabled}
                listItemHeight={10} listHeight={240}
                suffixIcon={<Arrow />}
            />
            <div className={'subnet-info'}>
                <Tooltip placement={'bottom'} title={'Learn how define a base IP address for a subnet correctly'}>
                    <Info />
                </Tooltip>
                <Link to={`${envKnowledgeBaseLink}/ip-calculation`} target={'_blank'}>Base IP explanation</Link>
            </div>
        </div>}
        </>;
};
